import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { BORDER } from "../utils/inventory.utils";


export function SelectLike({
  value,
  onChange,
  placeholder,
  options = [],
  className = "",
  searchable = false,
  searchPlaceholder = "Search...",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedOption = options.find(
    (o) => String(o.value ?? o.id ?? o) === String(value)
  );

  const filteredOptions = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return options;

    return options.filter((o) => {
      const label = String(o.label ?? o.name ?? o).toLowerCase();
      const val = String(o.value ?? o.id ?? o).toLowerCase();
      return label.includes(q) || val.includes(q);
    });
  }, [options, query]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={"relative z-30 " + className}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-[42px] w-full items-center justify-between rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[13px] text-slate-700 outline-none transition hover:border-[#d86a78] focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
      >
        <span className={selectedOption ? "text-slate-700" : "text-slate-400"}>
          {selectedOption?.label ?? selectedOption?.name ?? placeholder}
        </span>
        <ChevronDown size={16} className="text-slate-400" />
      </button>

      {open ? (
        <div className="absolute left-0 top-[46px] z-[9999] w-full overflow-hidden rounded-[12px] border border-[#f2c6cc] bg-white shadow-xl">
          {searchable ? (
            <div className="border-b border-[#f8dde1] p-2">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
              />
            </div>
          ) : null}

          <div className="max-h-[260px] overflow-auto py-1">
            <button
              type="button"
              onClick={() => handleSelect("")}
              className="flex w-full items-center px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-[#fff3f5]"
            >
              {placeholder}
            </button>

            {filteredOptions.length ? (
              filteredOptions.map((o) => {
                const optionValue = o.value ?? o.id ?? o;
                const optionLabel = o.label ?? o.name ?? o;
                const active = String(optionValue) === String(value);

                return (
                  <button
                    key={optionValue}
                    type="button"
                    onClick={() => handleSelect(optionValue)}
                    className={
                      "flex w-full items-center px-3 py-2 text-left text-[13px] hover:bg-[#fff3f5] " +
                      (active
                        ? "bg-[#fff3f5] font-semibold text-[#d86a78]"
                        : "text-slate-700")
                    }
                  >
                    {optionLabel}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-3 text-[13px] text-slate-500">
                No product found.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative min-w-0 flex-1">
      <Search
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-[42px] w-full rounded-[12px] border border-[#f2c6cc] bg-white pl-9 pr-4 text-[13px] text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
      />
    </div>
  );
}

export function ActionButton({
  children,
  onClick,
  variant = "primary",
  disabled = false,
  className = "",
}) {
  const styles =
    variant === "primary"
      ? "bg-[#d86a78] text-white hover:bg-[#c95b69]"
      : variant === "soft"
        ? "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff3f5]"
        : "bg-slate-900 text-white hover:bg-slate-800";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "inline-flex items-center justify-center gap-2 rounded-[12px] px-4 py-2.5 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 " +
        styles +
        " " +
        className
      }
    >
      {children}
    </button>
  );
}

export function StatusPill({ value }) {
  const status = String(value || "").toLowerCase();
  const cls =
    status === "approved" || status === "received" || status === "active"
      ? "bg-emerald-50 text-emerald-600"
      : status === "pending" ||
          status === "pending_batch" ||
          status === "partially_approved"
        ? "bg-amber-50 text-amber-700"
        : "bg-rose-50 text-rose-600";

  return (
    <span
      className={
        "inline-flex rounded-full px-3 py-1 text-[11px] font-semibold capitalize " +
        cls
      }
    >
      {value || "-"}
    </span>
  );
}

export function Card({ children, className = "" }) {
  return (
    <div
      className={"rounded-[20px] border bg-white shadow-sm " + className}
      style={{ borderColor: BORDER }}
    >
      {children}
    </div>
  );
}

export function SummaryCard({ icon: Icon, title, value, subtitle }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {title}
          </div>
          <div className="mt-2 text-[28px] font-bold text-slate-800">{value}</div>
          <div className="mt-1 text-[12px] text-slate-500">{subtitle}</div>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#fff1f3] text-[#d86a78]">
          <Icon size={22} />
        </div>
      </div>
    </Card>
  );
}

export function TabButton({ active, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-2 rounded-[12px] px-4 py-2.5 text-[13px] font-semibold transition " +
        (active
          ? "bg-[#d86a78] text-white shadow-sm"
          : "border border-[#f2c6cc] bg-white text-slate-600 hover:bg-[#fff7f8] hover:text-[#d86a78]")
      }
    >
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );
}

export function Pagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  className = "",
}) {
  if (totalPages <= 1) return null;

  const pages = [];
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  const normalizedStart = Math.max(1, end - 4);

  for (let page = normalizedStart; page <= end; page += 1) {
    pages.push(page);
  }

  return (
    <div
      className={
        "flex flex-col gap-3 border-t border-[#f4d8dc] px-5 py-4 sm:flex-row sm:items-center sm:justify-between " +
        className
      }
    >
      <div className="text-[12px] font-medium text-slate-500">
        Page {currentPage} of {totalPages}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="inline-flex h-9 min-w-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[13px] font-semibold text-slate-600 transition hover:bg-[#fff3f5] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft size={16} />
        </button>

        {pages.map((page) => (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            className={
              "inline-flex h-9 min-w-9 items-center justify-center rounded-[10px] px-3 text-[13px] font-semibold transition " +
              (page === currentPage
                ? "bg-[#d86a78] text-white"
                : "border border-[#f2c6cc] bg-white text-slate-600 hover:bg-[#fff3f5]")
            }
          >
            {page}
          </button>
        ))}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="inline-flex h-9 min-w-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[13px] font-semibold text-slate-600 transition hover:bg-[#fff3f5] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}